/**
 * Seeded Random Number Generator using Mulberry32 algorithm.
 *
 * IMPORTANT:
 * - This file is part of the deterministic simulation layer (`src/sim/**`).
 * - Do not import/use this RNG from rendering/UI code. The simulation owns it.
 */
export class SeededRNG {
  private state: number;
  private readonly initialSeed: number;

  private static _hashSeedParts(seedParts: string[]) {
    // djb2 over a structured string (with separators) to avoid accidental concatenation collisions.
    let hash = 5381;
    for (let i = 0; i < seedParts.length; i++) {
      const part = seedParts[i];
      // separator
      hash = ((hash << 5) + hash) + 0x7c; // '|'
      hash >>>= 0;
      for (let j = 0; j < part.length; j++) {
        hash = ((hash << 5) + hash) + part.charCodeAt(j);
        hash >>>= 0;
      }
    }
    return hash >>> 0;
  }

  /**
   * Creates a new SeededRNG instance.
   * Prefer passing a string array for robustness and reproducibility.
   */
  constructor(seed: number | string[]) {
    const seed32 = Array.isArray(seed) ? SeededRNG._hashSeedParts(seed) : (seed >>> 0);
    this.state = seed32 >>> 0;
    this.initialSeed = this.state;
  }

  /** Returns a pseudo-random number in [0, 1). */
  random(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Resets the RNG to its initial seed. */
  reset(): void {
    this.state = this.initialSeed;
  }

  /** Gets the current internal RNG state. */
  getState(): number {
    return this.state;
  }

  /** Sets the internal RNG state (for replay/rollback). */
  setState(state: number): void {
    this.state = state >>> 0;
  }
}


